import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useOnboarding } from '../../../contexts/OnboardingContext';
import SkillsAutocomplete from '../../../components/onboarding/SkillsAutocomplete';
import ResumeUploader from '../../../components/onboarding/ResumeUploader';
import ResumePreviewModal from '../../../components/onboarding/ResumePreviewModal';
import '../OnboardingWizard.css';

function SkillsExperience() {
  const { data, updateStepData, uploadResume, confirmParsedResume } = useOnboarding();
  const [uploading, setUploading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parseProgress, setParseProgress] = useState(0);
  const [parseMessage, setParseMessage] = useState('');
  const [parsedResume, setParsedResume] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const handleSkillsChange = (skills) => {
    updateStepData({ skills });
  };

  const handleParsingProgress = (status) => {
    setParseProgress(status.progress || 0);
    setParseMessage(status.message || 'Processing...');
  };

  const handleResumeUpload = async (file) => {
    setUploadError(null);
    setUploading(true);
    setParsing(false);
    setParseProgress(0);
    setParseMessage('');

    try {
      // Start upload - it will call onProgress with parsing status updates
      const result = await uploadResume(file, (status) => {
        if (status.status === 'parsing') {
          setUploading(false);
          setParsing(true);
          handleParsingProgress(status);
        }
      });

      setUploading(false);
      setParsing(false);

      if (!result.success) {
        console.error('Resume upload failed:', result.error);
        setUploadError(result.error || result.message || 'Upload failed');
        return;
      }

      // Check if resume was parsed successfully
      if (result.parsed && result.parsedResume) {
        setParsedResume(result.parsedResume);
        setShowPreview(true);
      } else if (result.message) {
        // Parsing failed but upload succeeded - show message
        console.log('Resume uploaded but not parsed:', result.message);
      }
    } catch (error) {
      console.error('Resume upload error:', error);
      setUploading(false);
      setParsing(false);
      setUploadError('An unexpected error occurred');
    }
  };

  const handleConfirmResume = async () => {
    setConfirming(true);
    const result = await confirmParsedResume();
    setConfirming(false);

    if (result.success) {
      setShowPreview(false);
      setParsedResume(null);
    } else if (result.hasExisting) {
      // User already has a master resume
      setShowPreview(false);
      setParsedResume(null);
    } else {
      console.error('Failed to confirm resume:', result.error);
    }
  };

  const handleClosePreview = () => {
    setShowPreview(false);
  };

  return (
    <div className="step-content">
      <div className="step-header">
        <h1 className="step-title">Your skills & experience</h1>
        <p className="step-subtitle">
          Help us understand your technical background.
        </p>
      </div>

      <div className="form-stack">
        {/* Skills */}
        <div className="form-group">
          <SkillsAutocomplete
            value={data.skills || []}
            onChange={handleSkillsChange}
            maxSkills={10}
            label="Add 3-5 of your strongest skills"
            placeholder="Type to search skills (e.g., React, Python)..."
          />
          <p className="form-hint">
            These help us match you with relevant jobs and interview questions.
          </p>
        </div>

        {/* Companies Worked */}
        <div className="form-group">
          <label className="form-label">
            Companies you've worked at <span className="label-optional">(optional)</span>
          </label>
          <input
            type="text"
            className="form-input"
            placeholder="e.g., Google, Meta, startup..."
            value={data.companies_worked || ''}
            onChange={(e) => updateStepData({ companies_worked: e.target.value })}
          />
        </div>

        {/* Resume Upload */}
        <div className="form-group">
          <label className="form-label">
            Upload your resume <span className="label-optional">(optional)</span>
          </label>
          <ResumeUploader
            onUpload={handleResumeUpload}
            uploading={uploading}
            parsing={parsing}
            parseProgress={parseProgress}
            parseMessage={parseMessage}
            uploaded={data.resume_uploaded}
            error={uploadError}
          />
        </div>
      </div>

      <p className="step-note">
        You can skip this step and add details later in your profile.
      </p>

      {/* Resume Preview Modal - rendered via portal to escape any overflow:hidden */}
      {showPreview && parsedResume && createPortal(
        <ResumePreviewModal
          parsedResume={parsedResume}
          onConfirm={handleConfirmResume}
          onClose={handleClosePreview}
          confirming={confirming}
        />,
        document.body
      )}
    </div>
  );
}

export default SkillsExperience;
